import * as React from 'react';
import styled, { css, useTheme } from 'styled-components/macro';
import { EditorState, Modifier } from 'draft-js';
import { IoCaretDown } from 'react-icons/io5';
import { theme } from 'lib/style';
import { useState } from 'react';
import { useOnClickOutside } from 'lib/utils';
import { useAuth } from 'lib/context';
import {
  getGeneralVariables,
  getLegacyVariables,
  getSalesVariables,
  getServiceVariables,
} from 'app/pages/library/videos/videoList/components/SendAndShare';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div<{ position?: string }>`
  ${({ position }) =>
    position === 'bottom' &&
    css`
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
    `}
`;
const DropdownWrapper = styled.div`
  position: relative;
`;
const DropdownContent = styled.div`
  position: absolute;
  z-index: 999;
  right: 0;
  top: 100%;
  width: 300px;
  background: ${({ theme }) => theme.colors.white[100]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[10]};
  box-sizing: border-box;
  box-shadow: 0px 6px 16px ${({ theme }) => theme.colors.black[5]};
  border-radius: 5px;
  padding: 8px 0;
  height: 180px;
  overflow: auto;
`;
const DropdownItem = styled.div`
  cursor: pointer;
  padding: 8px 24px;
  b,
  span {
    display: block;
  }
  b {
    margin-bottom: 4px;
    color: ${({ theme }) => theme.colors.primary[100]};
    font-weight: 600;
    font-size: 15px;
  }
  span {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primary[100]};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.secondary['non_alpha']};
  }
`;
type DropdownButtonProps = {
  textColor?: string;
  backgroundColor: string;
  borderColor: string;
  padding?: string;
  borderRadius?: string;
};
const DropdownButton = styled.button<DropdownButtonProps>`
  display: flex;
  align-items: center;
  padding: ${({ padding }) => padding || '10px 16px'};
  background: ${({ backgroundColor }) => backgroundColor};
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: ${({ borderRadius }) => borderRadius || '5px'};
  color: ${({ textColor, theme }) =>
    textColor ||
    theme.colors.secondary.text?.color ||
    theme.colors.secondary[100]};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
`;
const Caret = styled(IoCaretDown)`
  margin-left: 8px;
  &.up {
    transform: rotate(180deg);
  }
`;

type Props = {
  editorState?: EditorState;
  onEditorStateChange?: (
    editorState: EditorState,
    htmlEditorContent?: string
  ) => void;
  position?: string;
  text?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  onTextChange?: (text: string) => void;
  buttonPadding?: string;
  buttonBorderRadius?: string;
  dropdownButtonType?: 'button' | 'submit' | 'reset';
  extendStyles?: {
    wrapper?: React.CSSProperties;
    dropdownContent?: React.CSSProperties;
    button?: React.CSSProperties;
  };
};

export const AddVariableButton = ({
  editorState,
  onEditorStateChange,
  position,
  text,
  backgroundColor,
  textColor,
  borderColor = theme.palette.blue05,
  onTextChange,
  buttonPadding,
  buttonBorderRadius,
  dropdownButtonType = 'submit',
  extendStyles,
}: Props) => {
  const { t } = useTranslation();
  const themes = useTheme();
  const { ref } = useOnClickOutside(() => {
    setShowDropdown(false);
  });
  const { userData } = useAuth();
  let variables = getGeneralVariables(t);
  if (!userData.isAutomotive) {
    variables = [...variables, ...getLegacyVariables(t)];
  }
  if (userData.isAutomotiveSalesRole) {
    variables = [...variables, ...getSalesVariables(t)];
  }
  if (userData.isAutomotiveServiceRole) {
    variables = [...variables, ...getServiceVariables(t)];
  }

  const [showDropdown, setShowDropdown] = useState(false);
  const addText = (text: string) => {
    if (onTextChange) {
      onTextChange(text);
    }
    if (!!onEditorStateChange && !!editorState) {
      onEditorStateChange(
        EditorState.push(
          editorState,
          Modifier.replaceText(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            text,
            editorState.getCurrentInlineStyle()
          ),
          'insert-characters'
        )
      );
    }
    setShowDropdown(false);
  };

  return (
    <Wrapper
      ref={ref}
      position={position}
      style={{ ...extendStyles?.wrapper, zIndex: showDropdown ? 1 : 0 }}
    >
      <DropdownWrapper>
        <DropdownButton
          onClick={() => setShowDropdown(!showDropdown)}
          textColor={textColor}
          backgroundColor={
            backgroundColor || themes.colors.secondary['non_alpha']
          }
          borderColor={borderColor}
          padding={buttonPadding}
          borderRadius={buttonBorderRadius}
          style={extendStyles?.button}
          type={dropdownButtonType}
        >
          {text || t('Variables')}
          <Caret
            size={10}
            color={textColor}
            className={showDropdown ? 'up' : 'down'}
          />
        </DropdownButton>
        {showDropdown && (
          <DropdownContent style={extendStyles?.dropdownContent}>
            {variables.map((d, i) => (
              <DropdownItem
                key={`dropdown-item-${i}`}
                onClick={() => addText(d.value)}
              >
                <b>{d.value}</b>
                <span>{d.text}</span>
              </DropdownItem>
            ))}
          </DropdownContent>
        )}
      </DropdownWrapper>
    </Wrapper>
  );
};
